import { useRef, useEffect } from 'react';
import { Mic, MicOff, Video, VideoOff, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useWebinarStore from '@/stores/webinarStore';

export default function VideoGrid({ participants = [], localStream, remoteStreams, currentUserId, screenSharer }) {
  const isScreenMode = !!screenSharer;
  const safeParticipants = participants || [];
  const activeSpeakers = safeParticipants.filter(p => p.user_id !== screenSharer);
  const presenter = safeParticipants.find(p => p.user_id === screenSharer);

  // Determine grid layout
  const count = safeParticipants.length;
  const gridClass = count <= 1 ? 'grid-cols-1' :
                    count <= 2 ? 'grid-cols-2' :
                    count <= 4 ? 'grid-cols-2 grid-rows-2' :
                    count <= 6 ? 'grid-cols-3 grid-rows-2' :
                    count <= 9 ? 'grid-cols-3 grid-rows-3' :
                    'grid-cols-4 grid-rows-3';

  if (isScreenMode) {
    return (
      <div className="flex-1 flex gap-2 p-2 min-h-0">
        {/* Main screen share area */}
        <div className="flex-1 rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 relative">
          <VideoTile
            participant={presenter || { user_id: screenSharer, user_name: 'Presenter' }}
            stream={remoteStreams?.[screenSharer]}
            isLocal={screenSharer === currentUserId}
            localStream={localStream}
            isLarge
            isScreenShare
          />
        </div>
        {/* Sidebar with other participants */}
        <div className="w-48 flex flex-col gap-2 overflow-y-auto scrollbar-thin">
          {activeSpeakers.map(p => (
            <VideoTile
              key={p.user_id}
              participant={p}
              stream={remoteStreams?.[p.user_id]}
              isLocal={p.user_id === currentUserId}
              localStream={localStream}
              isSmall
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 grid ${gridClass} gap-2 p-2 min-h-0`}>
      <AnimatePresence mode="popLayout">
        {safeParticipants.map(p => (
          <motion.div
            key={p.user_id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            layout
          >
            <VideoTile
              participant={p}
              stream={remoteStreams?.[p.user_id]}
              isLocal={p.user_id === currentUserId}
              localStream={localStream}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function VideoTile({ participant, stream, isLocal, localStream, isLarge, isSmall, isScreenShare }) {
  const videoRef = useRef(null);
  const displayStream = isLocal ? localStream : stream;

  const storeIsMuted = useWebinarStore(s => s.isMuted);
  const storeIsCameraOn = useWebinarStore(s => s.isCameraOn);

  const isMuted = isLocal ? storeIsMuted : (participant?.is_muted !== false);
  const isCameraOn = isLocal ? storeIsCameraOn : (participant?.is_camera_on === true);

  useEffect(() => {
    if (videoRef.current && displayStream) {
      videoRef.current.srcObject = displayStream;
    }
  }, [displayStream, isCameraOn]);
  const name = participant?.user_name || 'Participant';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className={`relative rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 group ${
      isLarge ? 'h-full' : isSmall ? 'h-28' : 'h-full min-h-[120px]'
    }`}>
      {(isCameraOn || isScreenShare) && displayStream ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
          <div className={`rounded-full bg-gradient-to-br from-cyan-600 to-violet-600 flex items-center justify-center ${
            isSmall ? 'w-10 h-10 text-sm' : isLarge ? 'w-20 h-20 text-2xl' : 'w-14 h-14 text-lg'
          } font-bold text-white`}>
            {initials || <User className="w-6 h-6" />}
          </div>
        </div>
      )}

      {/* Overlay gradient */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent" />

      {/* Name + status */}
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={`text-white font-medium truncate ${isSmall ? 'text-[10px]' : 'text-xs'}`}>
            {isLocal ? `${name} (Vous)` : name}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isMuted ? (
            <div className="w-5 h-5 rounded-full bg-red-500/80 flex items-center justify-center">
              <MicOff className="w-3 h-3 text-white" />
            </div>
          ) : (
            <div className="w-5 h-5 rounded-full bg-emerald-500/80 flex items-center justify-center">
              <Mic className="w-3 h-3 text-white" />
            </div>
          )}
          {!isCameraOn && !isScreenShare && (
            <div className="w-5 h-5 rounded-full bg-zinc-600/80 flex items-center justify-center">
              <VideoOff className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
